import moment from 'moment-timezone';

import { Intervals } from '../types';

/**
 * Produce a specific, human-readable explanation of why a time segment falls
 * outside the given working-hours intervals — distinguishing "before you open",
 * "after you close", and "would run past closing" instead of a vague catch-all.
 *
 * @param start Segment start (ms epoch)
 * @param durationMs Segment duration (ms); 0 if unknown
 * @param workHours Absolute-timestamp open intervals for the relevant day
 * @param timezone Restaurant timezone (for formatting)
 * @param now Current time (ms epoch); injectable for testing
 */
export function describeOutOfBusinessHours(
  start: number,
  durationMs: number,
  workHours: Intervals,
  timezone: string,
  now: number = Date.now(),
): string {
  const end = start + (durationMs || 0);
  const fmt = (t: number): string => moment.tz(t, timezone).format('h:mm A');

  if (!workHours.length) {
    return `You're closed during this time.`;
  }

  // Sort intervals so "before open", "after close" and the gaps in between reflect
  // split hours (e.g. separate lunch and dinner service) rather than one flattened
  // open→close span, which would mislabel a time that falls in a mid-day closure.
  const intervals = [...workHours].sort((a, b) => a[0] - b[0]);
  const firstOpen = intervals[0][0];
  const lastClose = intervals[intervals.length - 1][1];

  if (start < firstOpen) {
    return `The start time (${fmt(start)}) is before you open (${fmt(firstOpen)}).`;
  }
  if (start > lastClose) {
    return `The start time (${fmt(start)}) is after you close (${fmt(lastClose)}).`;
  }

  const interval = intervals.find(h => start >= h[0] && start <= h[1]);
  if (interval) {
    if (end > interval[1]) {
      return `This would run until ${fmt(end)}, after you close (${fmt(interval[1])}).`;
    }
  } else {
    // start lands in a gap between two open periods (split hours).
    const prevClose = intervals.filter(h => h[1] < start).at(-1);
    const nextOpen = intervals.find(h => h[0] > start);
    if (prevClose && nextOpen) {
      return `You're closed between ${fmt(prevClose[1])} and ${fmt(nextOpen[0])}.`;
    }
  }

  if (start < now) {
    return `The selected time is in the past.`;
  }

  return `The selected time is outside your business hours.`;
}
